import React, { useState } from "react";
import { Grid, Button, CircularProgress, Stack, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import CustomSelect from "../../components/CustomSelect";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EmailIcon from '@mui/icons-material/Email';
import { api } from "../../helpers/apiRequest";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import colors from "../../theme/Colors";
import { exportToPDF, exportToExcel, getPDFBlob } from "./ReportUtils";
import EmailReportDialog from "../EmailReportDialog";

const ItemRateReport = ({ customers, items, styles }) => {
  const [loadingReport, setLoadingReport] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedEmailCustomer, setSelectedEmailCustomer] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [filters, setFilters] = useState({
    style: "",
    item: "",
    customer: "",
  });

  const generateReport = async (exportType = 'html') => {
    // Validate required fields
    if (!filters.customer || !filters.style || !filters.item) {
      toast.error("Customer, Style, and Item are required fields");
      return;
    }

    const params = new URLSearchParams();
    params.append('customer', filters.customer);
    params.append('style', filters.style);
    params.append('item', filters.item);
    
    const queryString = params.toString();
    
    setLoadingReport(true);
    try {
      const response = await api.get(
        `/reports/item-rate-report${queryString ? `?${queryString}` : ''}`
      );
      
      if (response.data && response.data.data) {
        const rate = response.data.data;
        const selectedCustomer = customers.find(c => c._id === filters.customer)?.name;
        const selectedStyle = styles.find(s => s._id === filters.style)?.name;
        const selectedItem = items.find(i => i._id === filters.item)?.name;
        
        if (exportType === 'excel') {
          const excelData = [{
            "Customer": rate.customer.name,
            "Party": rate.party.name,
            "Party Style": rate.p_style.name,
            "Style": rate.style.name,
            "Item": rate.item.name,
            "Current Rate": rate.price,
            "Last Updated": dayjs(rate.updatedAt).format("YYYY-MM-DD HH:mm"),
            "Updated By": rate.history[0]?.updated_by.name || "N/A"
          }];
          
          exportToExcel(excelData, `Item_Rate_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("Excel report generated successfully");
          return;
        }
        
        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Item Rate Report</title>
            <style>
               body { font-family: Arial, sans-serif; margin: 15px; padding: 50px 30px; align-items:center;-webkit-user-select: text;-moz-user-select: text;-ms-user-select: text; user-select: text;  }
             h1 { color: #1abc9c; text-align: center; }
              table { border-collapse: collapse; width: 95%; margin: 15px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin: 15px; }
              .report-filters { font-size: 14px; }
              .print-btn { background-color: #1abc9c; color: white; border: none; padding: 10px 20px; 
                          cursor: pointer; border-radius: 4px; margin-top: 20px; margin-right: 10px; }
              .export-btn-container { display: flex; margin-top: 20px; }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Item Rate Report</h1>
              <div class="report-filters">
                <p><strong>Customer:</strong> ${selectedCustomer}</p>
                <p><strong>Style:</strong> ${selectedStyle}</p>
                <p><strong>Item:</strong> ${selectedItem}</p>
              </div>
              <p>Generated on: ${dayjs().format("YYYY-MM-DD HH:mm")}</p>
            </div>
            
            <table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Party</th>
                  <th>Party Style</th>
                  <th>Style</th>
                  <th>Item</th>
                  <th>Current Rate</th>
                  <th>Last Updated</th>
                  <th>Updated By</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>${rate.customer.name}</td>
                  <td>${rate.party.name}</td>
                  <td>${rate.p_style.name}</td>
                  <td>${rate.style.name}</td>
                  <td>${rate.item.name}</td>
                  <td>${rate.price}</td>
                  <td>${dayjs(rate.updatedAt).format("YYYY-MM-DD HH:mm")}</td>
                  <td>${rate.history[0]?.updated_by.name || "N/A"}</td>
                </tr>
              </tbody>
            </table>
            
            <div class="export-btn-container">
            </div>
          </body>
          </html>
        `;
        
        if (exportType === 'pdf') {
          exportToPDF(htmlContent, `Item_Rate_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("PDF report generated successfully");
          return;
        }
        
        if (exportType === 'email') {
          setEmailDialogOpen(true);
          setLoadingReport(false);
          return;
        }
        
        const reportWindow = window.open('', '_blank');
        if (!reportWindow) {
          toast.error("Please allow popups for this site to view reports");
          return;
        }
        
        reportWindow.document.write(htmlContent);
        reportWindow.document.close();
        
        toast.success("Report generated successfully");
      } else {
        toast.error("No data available for the selected filters");
      }
    } catch (error) {
      console.error("Error generating item rate report:", error);
      toast.error("Failed to generate item rate report");
    } finally {
      setLoadingReport(false);
    }
  };

  const sendReportByEmail = async () => {
    if (!selectedEmailCustomer) {
      toast.error('Please select a customer');
      return;
    }

    // Validate required fields
    if (!filters.customer || !filters.style || !filters.item) {
      toast.error("Customer, Style, and Item are required fields");
      return;
    }

    setSendingEmail(true);
    try {
      // Generate the report HTML
      const params = new URLSearchParams();
      params.append('customer', filters.customer);
      params.append('style', filters.style);
      params.append('item', filters.item);
      
      const queryString = params.toString();
      
      const response = await api.get(`/reports/item-rate-report${queryString ? `?${queryString}` : ''}`);
      
      if (response.data && response.data.data) {
        const rate = response.data.data;
        const selectedCustomer = customers.find(c => c._id === filters.customer)?.name;
        const selectedStyle = styles.find(s => s._id === filters.style)?.name;
        const selectedItem = items.find(i => i._id === filters.item)?.name;
        
        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Item Rate Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              h1 { color: #1abc9c; }
              table { border-collapse: collapse; width: 100%; margin-top: 20px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin-bottom: 20px; }
              .report-filters { margin-bottom: 15px; font-size: 14px; }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Item Rate Report</h1>
              <div class="report-filters">
                <p><strong>Customer:</strong> ${selectedCustomer}</p>
                <p><strong>Style:</strong> ${selectedStyle}</p>
                <p><strong>Item:</strong> ${selectedItem}</p>
              </div>
              <p>Generated on: ${dayjs().format("YYYY-MM-DD HH:mm")}</p>
            </div>
            
            <table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Party</th>
                  <th>Party Style</th>
                  <th>Style</th>
                  <th>Item</th>
                  <th>Current Rate</th>
                  <th>Last Updated</th>
                  <th>Updated By</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>${rate.customer.name}</td>
                  <td>${rate.party.name}</td>
                  <td>${rate.p_style.name}</td>
                  <td>${rate.style.name}</td>
                  <td>${rate.item.name}</td>
                  <td>${rate.price}</td>
                  <td>${dayjs(rate.updatedAt).format("YYYY-MM-DD HH:mm")}</td>
                  <td>${rate.history[0]?.updated_by.name || "N/A"}</td>
                </tr>
              </tbody>
            </table>
          </body>
          </html>
        `;
        
        // Convert HTML to PDF blob
        const pdfBlob = await getPDFBlob(htmlContent);
        
        // Create FormData and append the PDF blob
        const formData = new FormData();
        formData.append('image', pdfBlob, `Item_Rate_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.pdf`);
        formData.append('userId', selectedEmailCustomer);
        formData.append('type', "Report");
        // Send the email
        const emailResponse = await api.post('inventory/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (emailResponse.status === 200) {
          toast.success('Report sent by email successfully');
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        } else {
          toast.error('Failed to send report by email');
        }
      } else {
        toast.error('No data available for the selected filters');
      }
    } catch (error) {
      console.error('Error sending report by email:', error);
      toast.error('Failed to send report by email');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <Grid container spacing={3} alignItems="center">
      <Grid item xs={12} sm={3}>
        <CustomSelect
          label="Customer *"
          value={filters.customer}
          onChange={(e) => setFilters({ ...filters, customer: e.target.value })}
          options={customers.map((customer) => ({ id: customer._id, name: customer.name }))}
          fullWidth
          required
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <FormControl fullWidth required>
          <InputLabel>Style</InputLabel>
          <Select
            value={filters.style}
            label="Style *"
            onChange={(e) => setFilters({ ...filters, style: e.target.value })}
          >
            {styles.map((style) => (
              <MenuItem key={style._id} value={style._id}>
                {style.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControl fullWidth required>
          <InputLabel>Item</InputLabel>
          <Select
            value={filters.item}
            label="Item *"
            onChange={(e) => setFilters({ ...filters, item: e.target.value })}
          >
            {items.map((item) => (
              <MenuItem key={item._id} value={item._id}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={12} sm={2}>
        <Stack spacing={1}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => generateReport('html')}
            disabled={loadingReport || sendingEmail}
            startIcon={<DescriptionIcon />}
            sx={{ 
              height: "38px", 
              backgroundColor: colors.primary, 
              '&:hover': { 
                backgroundColor: colors.primary, 
                opacity: 0.9 
              } 
            }}
          >
            {loadingReport ? <CircularProgress size={24} color="inherit" /> : "View"}
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateReport('pdf')}
            disabled={loadingReport || sendingEmail}
            startIcon={<PictureAsPdfIcon />}
            sx={{ 
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': { 
                borderColor: colors.primary,
                opacity: 0.9 
              } 
            }}
          >
            PDF
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateReport('excel')}
            disabled={loadingReport || sendingEmail}
            startIcon={<FileDownloadIcon />}
            sx={{ 
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': { 
                borderColor: colors.primary,
                opacity: 0.9 
              } 
            }}
          >
            Excel
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateReport('email')}
            disabled={loadingReport || sendingEmail}
            startIcon={<EmailIcon />}
            sx={{ 
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': { 
                borderColor: colors.primary,
                opacity: 0.9 
              } 
            }}
          >
            Email
          </Button>
        </Stack>
      </Grid>

      <EmailReportDialog
        open={emailDialogOpen}
        handleClose={() => {
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        }}
        selectedCustomer={selectedEmailCustomer}
        setSelectedCustomer={setSelectedEmailCustomer}
        customers={customers}
        isLoading={sendingEmail}
        handleSendEmail={sendReportByEmail}
      />
    </Grid>
  );
};

export default ItemRateReport;
